import { Chip, MenuItem, TextField } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { eventClick, RefreshData } from "components/helper";
import { P } from "const/permissions";
import { usePermission } from "hooks/usePermissions";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useMemo, useState } from "react";
import ReferredClientDrawer from "./ReferredClientDrawer";
import ClientProfileLink from "../ClientProfileLink";
import { listReferralLeads } from "../referralLeadsApi";
import { useReferralOverview } from "../referralOverviewApi";

const STATES = ["", "SENT_TO_ORG", "ACTIVATED", "REJECTED"];

const STATE_TONES = { SENT_TO_ORG: "info", ACTIVATED: "success", REJECTED: "error" };

/**
 * The office side of referred leads: what partners sent over, waiting for activation or
 * returned with a reason. Only leads a partner actually sent appear here.
 */
const ReferredClients = () => {
  const queryClient = useQueryClient();
  const canActivate = usePermission(P.REFERRAL.LEAD_ACTIVATE);
  const canReject = usePermission(P.REFERRAL.LEAD_REJECT);
  const canAssignOfficer = usePermission(P.CLIENTS.ASSIGN_OFFICER);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [search, setSearch] = useState("");
  const [state, setState] = useState("SENT_TO_ORG");
  const [detailId, setDetailId] = useState(null);

  const filters = useMemo(
    () => ({
      page: pagination.pageIndex,
      size: pagination.pageSize,
      search,
      state,
    }),
    [pagination.pageIndex, pagination.pageSize, search, state]
  );

  const { data, isLoading, isFetching, refetch } = useQuery(
    ["referral-leads", filters],
    () => listReferralLeads(filters),
    { keepPreviousData: true, refetchOnMount: true }
  );

  // Same cache key as the sidebar badge: the header number costs no extra request.
  const { data: overview } = useReferralOverview();
  const pendingCount = Number(overview?.pendingLeads ?? 0);

  const refresh = () => {
    refetch();
    queryClient.invalidateQueries(["referral-leads"]);
    queryClient.invalidateQueries(["referral-overview"]);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 150,
        Cell: ({ row }) => (
          <ClientProfileLink clientId={row.original.convertedClientId}>
            {row.original.name}
          </ClientProfileLink>
        ),
      },
      { accessorKey: "email", header: "Email", size: 180 },
      { accessorKey: "number", header: "Phone", size: 130 },
      { accessorKey: "partnerName", header: "Partner", size: 150 },
      {
        accessorKey: "state",
        header: "State",
        size: 120,
        Cell: ({ row }) => (
          <Chip
            label={row.original.state}
            color={STATE_TONES[row.original.state] || "default"}
            size="small"
          />
        ),
      },
      {
        accessorKey: "sentToOrgAt",
        header: "Sent",
        size: 130,
        Cell: ({ row }) =>
          row.original.sentToOrgAt ? new Date(row.original.sentToOrgAt).toLocaleDateString() : "—",
      },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: data?.content || [],
    rowCount: data?.totalItems || 0,
    manualPagination: true,
    manualFiltering: true,
    onPaginationChange: setPagination,
    state: { pagination, isLoading, showProgressBars: isFetching },
    enableStickyHeader: true,
    muiTableContainerProps: { sx: { maxHeight: "70vh", minHeight: "70vh", overflowY: "auto" } },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => {
        if (!eventClick(event)) setDetailId(row.original.id);
      },
      sx: { cursor: "pointer", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.04)" } },
    }),
  });

  return (
    <div className="flex flex-wrap mt-4 dashBody">
      <div className="w-full mb-12 px-4">
        <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded bg-app-surface text-app-text">
          <div className="rounded-t mb-0 px-4 py-3 border-0">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="font-semibold text-lg text-app-text">
                Referred clients ({data?.totalItems ?? 0})
                {pendingCount > 0 ? ` — ${pendingCount} waiting` : ""}{" "}
                <RefreshData refreshData={refetch} />
              </h3>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-3">
              <TextField
                size="small"
                label="Search name or email"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 240 }}
              />
              <TextField
                size="small"
                select
                label="State"
                value={state}
                onChange={(e) => setState(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                {STATES.map((option) => (
                  <MenuItem key={option || "all"} value={option}>
                    {option || "All"}
                  </MenuItem>
                ))}
              </TextField>
            </div>
          </div>
          <div className="block w-full overflow-x-auto">
            <MaterialReactTable table={table} />
          </div>
        </div>
      </div>

      <ReferredClientDrawer
        leadId={detailId}
        canActivate={canActivate}
        canReject={canReject}
        canAssignOfficer={canAssignOfficer}
        onClose={() => setDetailId(null)}
        onChanged={refresh}
      />
    </div>
  );
};

export default ReferredClients;
